<template>
  <span
    v-if="visible"
    class="sidebar-nav-badge"
    :style="{ backgroundColor: resolvedColor, color: resolvedTextColor }"
    :aria-label="ariaLabel"
  >
    <slot>{{ displayValue }}</slot>
  </span>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useColors } from 'vuestic-ui'

const props = withDefaults(
  defineProps<{
    /** Numeric count or custom label shown inside the badge. */
    count?: number | string
    /** Vuestic color token (`danger`, `warning`, `primary`, …) or any CSS color. */
    color?: string
    /** Vuestic color token or CSS color for the badge text. */
    textColor?: string
    hideWhenZero?: boolean
    ariaLabel?: string
  }>(),
  {
    count: 0,
    color: 'danger',
    textColor: '#fff',
    hideWhenZero: true,
    ariaLabel: undefined,
  },
)

const { getColor } = useColors()

const numericCount = computed(() => {
  const n = Number(props.count)
  return Number.isFinite(n) ? n : null
})

const visible = computed(() => {
  if (!props.hideWhenZero) {
    return props.count !== '' && props.count != null
  }
  if (numericCount.value != null) {
    return numericCount.value > 0
  }
  return Boolean(props.count)
})

const displayValue = computed(() => props.count)
const resolvedColor = computed(() => getColor(props.color))
const resolvedTextColor = computed(() => getColor(props.textColor))
</script>

<style scoped>
.sidebar-nav-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.25rem;
  height: 1.25rem;
  padding: 0 0.35rem;
  border-radius: 999px;
  font-size: 0.6875rem;
  font-weight: 700;
  line-height: 1;
  flex-shrink: 0;
}
</style>
